import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
export interface FilterOption {
  label: string;
  value: string | null;
}
interface FilterMenuProps {
  label: string;
  options: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Label shown when nothing is selected, e.g. "All years". */
  allLabel: string;
}

/**
 * Compact filter control: a single-line trigger that expands into a short list,
 * so a filter set never wraps across multiple rows of pills.
 */
export function FilterMenu({
  label,
  options,
  value,
  onChange,
  allLabel
}: FilterMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = options.find(option => option.value === value);
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);
  return <div ref={containerRef} className="relative" data-id="element-531" data-mp="qqfdnn7q4gfs">
      <button type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(current => !current)} className={`inline-flex min-h-tap items-center gap-2 rounded-pill border px-4 text-small transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${value ? 'border-primary bg-primary-soft font-medium text-primary-text' : 'border-rule bg-card text-ink hover:bg-canvas'}`} data-id="element-532" data-mp="3pbglo36xpkna">
        <span className="text-ink-muted" data-id="element-533" data-mp="3bgo1grg3ngad">{label}</span>
        <span data-id="element-534" data-mp="33h05lo1t57e5">{active ? active.label : allLabel}</span>
        <ChevronDownIcon aria-hidden="true" className="h-4 w-4 opacity-70" data-id="element-535" data-mp="2yj81lvnw1utl" />
      </button>

      {open ? <div role="listbox" aria-label={label} className="absolute left-0 top-[calc(100%+0.375rem)] z-20 flex w-56 flex-col rounded-field border border-rule bg-card p-1 shadow-raised" data-id="element-536" data-mp="3d7up4kno5svw">
          {options.map(option => {
        const selected = option.value === value;
        return <button key={option.label} type="button" role="option" aria-selected={selected} onClick={() => {
          onChange(option.value);
          setOpen(false);
        }} className={`flex min-h-tap items-center justify-between gap-3 rounded-control px-3 text-left text-small hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${selected ? 'font-medium text-primary-text' : 'text-ink'}`} data-id="element-537" data-mp="2flnlbcz6w4kl">
                {option.label}
                {selected ? <CheckIcon aria-hidden="true" className="h-4 w-4" data-id="element-538" data-mp="3bf43mgwm706" /> : null}
              </button>;
      })}
        </div> : null}
    </div>;
}